/* Sticky buy button start */
class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
    this._observer = null;

    this._mq = window.matchMedia("(max-width: 767px)");
    this._onMqChange = this._onMqChange.bind(this);

    this._onButtonClick = this._onButtonClick.bind(this);
    this._onFormChange = this._onFormChange.bind(this);
    this._onVariantChangeEvent = this._onVariantChangeEvent.bind(this);

    this._btn = null;
    this._addToCartModule = null;
    this._form = null;
    this._addToCart = false;

    this._priceEl = null;
    this._compareEl = null;

    this._priceObserver = null;
    this._priceRoot = null;
    this._syncRaf = 0;
  }

  connectedCallback() {
    this.initialize();
    this._initPriceSync();
  }

  disconnectedCallback() {
    this.destroy();
  }

  destroy() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._mq) {
      this._mq.removeEventListener?.("change", this._onMqChange);
      this._mq.removeListener?.(this._onMqChange);
    }

    if (this._btn) {
      this._btn.removeEventListener("click", this._onButtonClick);
    }

    if (this._form) {
      this._form.removeEventListener("change", this._onFormChange, true);
    }

    document.removeEventListener("variant:change", this._onVariantChangeEvent);

    if (this._priceObserver) {
      this._priceObserver.disconnect();
      this._priceObserver = null;
    }

    if (this._syncRaf) {
      cancelAnimationFrame(this._syncRaf);
      this._syncRaf = 0;
    }

    this._btn = null;
    this._addToCartModule = null;
    this._form = null;
    this._priceEl = null;
    this._compareEl = null;
    this._priceRoot = null;
  }

  _onMqChange() {
    this.destroy();
    this.initialize();
    this._initPriceSync();
  }

  initialize() {
    this._addToCartModule = document.querySelector(".wt-product__add-to-cart");
    this._btn = this.querySelector("button");
    this._addToCart = this.dataset.addToCart === "";

    if (!this._addToCartModule || !this._btn) return;

    if (!this._mq.matches) {
      this.classList.remove(this.activeClass);
      this._mq.addEventListener?.("change", this._onMqChange);
      this._mq.addListener?.(this._onMqChange);
      return;
    }

    this._mq.addEventListener?.("change", this._onMqChange);
    this._mq.addListener?.(this._onMqChange);

    this._btn.removeEventListener("click", this._onButtonClick);
    this._btn.addEventListener("click", this._onButtonClick);

    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.classList.remove(this.activeClass);
        else this.classList.add(this.activeClass);
      },
      { root: null, threshold: 0.01, rootMargin: "0px 0px -10% 0px" }
    );

    this._observer.observe(this._addToCartModule);
  }

  _onButtonClick(e) {
    if (!this._addToCart) e.preventDefault();
    this._addToCartModule?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  _initPriceSync() {
    this._priceEl = this.querySelector("[data-sticky-price]");
    this._compareEl = this.querySelector("[data-sticky-compare]");

    const formId = this._btn?.getAttribute("form");
    this._form = formId ? document.getElementById(formId) : this.closest("form");

    document.removeEventListener("variant:change", this._onVariantChangeEvent);
    document.addEventListener("variant:change", this._onVariantChangeEvent);

    if (this._form) {
      this._form.removeEventListener("change", this._onFormChange, true);
      this._form.addEventListener("change", this._onFormChange, true);
    }

    this._startMainPriceMirror();
    this._syncStickyFromMainPrice();
  }

  _onVariantChangeEvent(event) {
    const variant = event?.detail?.variant;
    if (!variant) return;
    this._setPrices(variant.price, variant.compare_at_price);
  }

  _onFormChange() {
    this._syncStickyFromMainPrice();
  }

  _startMainPriceMirror() {
    if (this._priceObserver) {
      this._priceObserver.disconnect();
      this._priceObserver = null;
    }

    this._priceRoot =
      this.closest("section")?.querySelector('[id^="price-"]') ||
      document.querySelector('[id^="price-"]') ||
      document.querySelector(".wt-product__price") ||
      document.querySelector("[data-price-wrapper]") ||
      document.querySelector(".price") ||
      document.querySelector("[data-product-price]");

    if (!this._priceRoot || !this._priceEl) return;

    this._priceObserver = new MutationObserver(() => {
      if (this._syncRaf) return;
      this._syncRaf = requestAnimationFrame(() => {
        this._syncRaf = 0;
        this._syncStickyFromMainPrice();
      });
    });

    this._priceObserver.observe(this._priceRoot, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
  }

  _syncStickyFromMainPrice() {
    if (!this._priceRoot || !this._priceEl) return;

    const priceNode =
      this._priceRoot.querySelector(".price-item--regular") ||
      this._priceRoot.querySelector(".price__regular .price-item") ||
      this._priceRoot.querySelector("[data-regular-price]") ||
      this._priceRoot.querySelector("[data-product-price]") ||
      this._priceRoot.querySelector(".wt-product__price__final");

    const compareNode =
      this._priceRoot.querySelector(".price__regular s") ||
      this._priceRoot.querySelector(".price-item--regular s") ||
      this._priceRoot.querySelector("[data-compare-price]") ||
      this._priceRoot.querySelector(".wt-product__price__compare") ||
      this._priceRoot.querySelector("s:not(.wt-product__sticky-compare)");

    const priceText = priceNode?.textContent?.trim();
    const compareText = compareNode?.textContent?.trim();

    if (priceText) this._priceEl.textContent = priceText;

    if (this._compareEl) {
      const hasCompare = !!compareText && !!priceText && compareText !== priceText;

      if (hasCompare) {
        this._compareEl.textContent = compareText;
        this._compareEl.style.display = "inline-block";
      } else {
        this._compareEl.style.display = "none";
      }
    }
  }

  _setPrices(priceCents, compareAtCents) {
    if (!this._priceEl) return;

    const formattedPrice = this._formatMoney(priceCents);
    this._priceEl.textContent = formattedPrice;

    if (this._compareEl) {
      const hasCompare =
        typeof compareAtCents === "number" &&
        typeof priceCents === "number" &&
        compareAtCents > priceCents;

      if (hasCompare) {
        this._compareEl.textContent = this._formatMoney(compareAtCents);
        this._compareEl.style.display = "";
      } else {
        this._compareEl.style.display = "none";
      }
    }
  }

  _formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      const fmt = window.theme?.moneyFormat || window.money_format || "{{amount}}";
      return window.Shopify.formatMoney(cents, fmt);
    }
    return (Number(cents) / 100).toFixed(2);
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);
/* Sticky buy button end */

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
